import { useMemo } from 'react';
import { useAccesPort } from '@taki/ui';

import { withoutInaccessibleApps } from './acces.js';
import { useDeprecatedAppIds, type DeprecatedAppsState } from './buildStamp.js';
// [MENU DEV] La SEULE ligne de ce fichier qui parte avec la greffe. Le jour du
// `rm -rf packages/shell/src/dev/`, on retire cet import et l'appel de
// `useVisibleAppEntries` plus bas : le reste du fichier est permanent.
import { useVisibleAppEntries } from './dev/appVisibility.js';
import type { ShellAppEntry } from './types.js';

/**
 * QUELLES APPS S'AFFICHENT, et le seul endroit qui le decide.
 *
 * POURQUOI CE FICHIER EXISTE. Cinq surfaces montrent la liste des apps : le
 * dock, le menu Home, les onglets du hub Parametres, le panneau Dock, et le
 * libelle "Retour a <app>". Avant, chacune appelait le filtre DEV directement
 * et aucune ne connaissait l'etat deprecated. Une regle appliquee a cinq
 * endroits est une regle qui finira par ne plus l'etre au cinquieme.
 *
 * TROIS FILTRES, DANS CET ORDRE, et ils ne sont pas de meme nature :
 *  1. `deprecated` : PERMANENT. Une app morte disparait des listes chez un
 *     client, pas seulement sur un poste de developpement.
 *  2. l'ACCES : PERMANENT. Une app a laquelle l'utilisateur n'a aucun droit
 *     (`niveauApp === 'none'`) ne s'affiche pas. Du masquage, jamais de la
 *     securite : les rules refusent la lecture de toute facon.
 *  3. le masquage DEV : JETABLE. Un confort de poste, compile hors production.
 *
 * CE QU'IL NE FILTRE JAMAIS, et c'est deliberé : la RESOLUTION DE ROUTE. Une
 * app deprecated reste ouvrable par son adresse et par l'historique, avec sa
 * bande rouge. Filtrer la route ferait tomber "App introuvable" sur l'ecran de
 * quelqu'un au moment ou l'app est marquee, ce qui est pire que ce qu'on
 * corrige. La desinstallation reelle reste `installedApps`.
 */

/**
 * Les apps vivantes, deprecated exclues. PURE, testable sans React.
 *
 * REND LA MEME REFERENCE quand rien n'est exclu, et ce n'est pas une
 * micro-optimisation : les appelants la passent a des `useMemo`, et un `filter`
 * inconditionnel fabriquerait un tableau neuf a chaque rendu, donc un dock qui
 * se recalcule sans arret.
 */
export function withoutDeprecatedApps(
  state: DeprecatedAppsState,
  entries: readonly ShellAppEntry[],
): readonly ShellAppEntry[] {
  if (state.kind !== 'known' || state.ids.size === 0) return entries;
  const vivantes = entries.filter((entry) => !state.ids.has(entry.id));
  return vivantes.length === entries.length ? entries : vivantes;
}

/**
 * LA LISTE A AFFICHER, pour toute surface qui montre des apps.
 *
 * ATTENTION AU PANNEAU DOCK : il ne suffit pas de lui donner cette liste. Il
 * REECRIT ses preferences a partir de ce qu'il recoit, donc un id absent de la
 * liste serait efface de la pref au premier clic. Il utilise cette liste pour
 * AFFICHER et la pref complete pour ECRIRE (voir `preserveHiddenDockApps`).
 */
export function useListedAppEntries(
  entries: readonly ShellAppEntry[],
): readonly ShellAppEntry[] {
  const deprecated = useDeprecatedAppIds();
  const acces = useAccesPort();
  const vivantes = useMemo(
    () => withoutDeprecatedApps(deprecated, entries),
    [deprecated, entries],
  );
  /*
   * L'ACCES, APRES `deprecated` ET AVANT LE MASQUAGE DEV.
   *
   * TANT QUE LA FICHE N'EST PAS LUE (`pret` faux), ON NE RETIRE RIEN, et sans
   * port non plus : le shell d'avant ce chantier montrait tout, et une app
   * qui disparait une demi-seconde apres l'ouverture se lit comme un bug. On
   * ne masque que sur une information qu'on a vraiment.
   */
  const accessibles = useMemo(
    () =>
      withoutInaccessibleApps(
        vivantes,
        acces !== null && acces.pret ? (appId) => acces.niveauApp(appId) : null,
      ),
    [vivantes, acces],
  );
  // [MENU DEV] Le troisieme filtre, jetable avec la greffe.
  return useVisibleAppEntries(accessibles);
}

/**
 * PRESERVER LES IDS QUE LA LISTE AFFICHEE NE CONTIENT PLUS, dans une pref
 * d'ordre et de selection (`dock.apps`).
 *
 * LE MODE DE PANNE QU'ELLE EVITE, et il est deja arrive sur ce depot : le
 * panneau Dock reecrit la pref a partir de la liste qu'il affiche. Donnez-lui
 * une liste filtree, et le premier clic efface l'epinglage de toute app
 * filtree, sans que rien ne le dise. Le masquage doit rester cosmetique et
 * reversible ; effacer une preference, ce n'est ni l'un ni l'autre.
 *
 * Les ids preserves sont reposes en QUEUE : ils ne prennent aucune place dans
 * la barre (`resolveDockApps` saute un id non resolu AVANT de compter le
 * plafond), et ils reviennent a leur place le jour ou l'app redevient visible.
 */
export function preserveHiddenDockApps(
  pref: readonly string[] | null,
  nextOrder: readonly string[],
  nextSelected: ReadonlySet<string>,
  listedIds: ReadonlySet<string>,
): string[] {
  const gardes = (pref ?? []).filter((id) => !listedIds.has(id));
  const choisis = nextOrder.filter((id) => nextSelected.has(id));
  return [...choisis, ...gardes];
}

/**
 * La meme protection pour les RACCOURCIS du dock (`dock.shortcuts`).
 *
 * C'est le piege jumeau, et il est plus discret : le panneau resout les
 * raccourcis contre la liste recue, laisse tomber ceux dont l'app est absente,
 * puis reecrit la pref a partir de ce qui reste. Trois gestes le declenchent
 * (deplacer, retirer, ajouter), pas seulement le retrait.
 */
export function preserveHiddenDockShortcuts<T extends { appId: string }>(
  pref: readonly T[] | null,
  next: readonly T[],
  listedIds: ReadonlySet<string>,
): T[] {
  const gardes = (pref ?? []).filter((raccourci) => !listedIds.has(raccourci.appId));
  return [...next, ...gardes];
}
